<template lang="pug">
b-form
  //- small {{ showOtp2faBound }} - {{ get2faValidated }} 
  // Status Display - Show when already bound
  // check state get2faValidated from store together with showOtp2faBound
  // Only render after initialization to prevent blink
  // Use showOtp2faBound as single source of truth (initialized from store, updated by API)
  // get2faValidated is checked as fallback only during initial load before API completes
  template(v-if="isInitialized && showOtp2faBound")   
    b-alert.mb-0(variant="success" show)
      .d-flex.justify-content-between.align-items-center
        strong
          i.fas.fa-check-circle.mr-2
          | {{ $t("ui.two_factor_authenticator_bound") }}
        SubmitButton(
          :loading="loading"
          icon="fa fa-unlock"
          :text="$t('ui.unbind')"
          variant="danger"
          isModal=false
          type="button"
          @click="unbindOTP2fa()"
          :disabled="$helpers.isSuspended()"
        )
    //- b-alert.mt-3(variant="warning" show)
    //-   strong
    //-     i.fas.fa-exclamation-triangle.mr-2
    //-     | {{ $t("message.otp_unbind_first_to_use_other", { module: $t("ui.setup_otp_via_telegram_chatbot") }) }} 
  // 2FA Binding Flow - Show when not bound
  template(v-else-if="isInitialized")
    //- b-card-header
    //-   h5.mb-0 {{ $t("ui.setup_otp_via_mobile_authenticator") }}
    // Step 1: Download Google Authenticator
    .form-group.mb-5
      h6.font-weight-bold.mb-3 {{ $t("ui.step_1_download_google_authenticator") }}
      .google-auth-section
        .google-auth-branding
          .d-flex
            .google-auth-logo
              img(
                src="/assets/img/google-authenticator.png"
                alt="Google Authenticator"
              )
            .brand-content
              .brand-text.mb-3
                .brand-name Google Authenticator
                .brand-subtitle Google
              .download-buttons.d-flex.flex-wrap
                a.download-btn.app-store(
                  href="https://apps.apple.com/app/google-authenticator/id388497605"
                  target="_blank"
                  rel="noopener noreferrer"
                )
                  img(
                    src="/assets/img/appstore.png"
                    alt="Download on App Store"
                  )
                a.download-btn.google-play(
                  href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                  target="_blank"
                  rel="noopener noreferrer"
                )
                  img(
                    src="/assets/img/googleplay.png"
                    alt="Get it on Google Play"
                  )
        .additional-info
          p.text-muted.mb-1 * {{ $t("message.other_authenticator_apps_supported") }}
          p.text-muted.mb-0 * {{ $t("message.date_time_automatic_required") }}

    // Step 2: QR Code
    .form-group.mb-5(v-if="qrCodeData")
      h6.font-weight-bold.mb-3 {{ $t("ui.step_2_create_account_scan_qr") }}
      .qr-code-container.text-center.mb-3
        img.qr-code-image(
          :src="qrCodeData"
          alt="QR Code"
        )

    // Step 3: Enter 6-digit Code
    .form-group.mb-4
      h6.font-weight-bold.mb-3 {{ $t("ui.step_3_enter_6_digit_code_verify") }}
      .otp-input-section
        label.mb-2 {{ $t("ui.otp") }}
        DebounceInput(
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          ref="verification_code"
          :dirty="$v.verification_code.$dirty"
          :error="$v.verification_code.$error"
          :placeholder='$t("ui.enter_6_digit_code")'
          v-model.trim.lazy="$v.verification_code.$model"
          :maxLength="6"
          :disabled="loading || !qrCodeData"
          class="otp-input-field"
        )
        ValidateResult(:value="$v.verification_code" :name="$t('ui.verification_code')")
      .verify-section.mt-3
        b-button(
          variant="primary"
          @click="verifyOTP2fa()"
          :disabled="loading || !qrCodeData || !enableOTP"
        )
          | {{ $t("ui.verify") }}
      .enable-checkbox.mt-3
        b-form-checkbox(v-model="enableOTP")
          | {{ $t("ui.enable_otp_via_mobile_authenticator_app") }}

    // Error Message
    b-alert.alert-sm.animated.flash(:show="!submission.success" variant="danger") {{ $t(submission.status) }}

  // Unbind OTP Modal
  UnbindOTP2fa(ref="unbindOTPModal" @unbind-success="onUnbindSuccess")

</template>

<script>
import { required, numeric, minLength, maxLength } from "vuelidate/lib/validators";
import SubmitButton from "@/library/SubmitButton";
import ValidateResult from "@/library/ValidateResult";
import DebounceInput from "@/library/DebounceInput";
import UnbindOTP2fa from "@/components/settings/unbindOTP2fa";

export default {
  components: {
    SubmitButton,
    ValidateResult,
    DebounceInput,
    UnbindOTP2fa
  },
  data() {
    return {
      isInitialized: false,
      showOtp2faBound: false,
      qrCodeData: null,
      manualSecret: null,
      verification_code: "",
      showManualEntry: false,
      enableOTP: false,
      loading: false,
      submission: {
        success: true,
        status: ""
      },
      allowUnbind: true
    };
  },
  computed: {
    get2faValidated() {
      return this.$store.getters.get2faValidated;
    }
  },
  created() {
    // Initialize from store value (set during login) to prevent flash
    // This will be updated by loadBindingStatus() with fresh API data
    this.showOtp2faBound = this.get2faValidated === true || this.get2faValidated === 1;
  },
  validations: {
    verification_code: {
      required,
      numeric,
      minLength: minLength(6),
      maxLength: maxLength(6)
    }
  },
  mounted() {
    this.loadBindingStatus();
  },
  methods: {
    reset() {
      this.$v.$reset();
      this.submission.success = true;
      this.submission.status = "";
    },
    
    async loadBindingStatus() {
      // Load current binding status from backend
      try {
        const response = await this.$store.dispatch("user/getOTP2faGet");
        if (response.success && response.data) {
          // Check if 2fa_validated is true or false
          // response.data is the status object: { "2fa_validated": true/false }
          const twoFactorValidated = response.data[0]['2fa_validated'];
          this.showOtp2faBound = twoFactorValidated === true;
          // Update store state to ensure reactivity in parent components
          this.$set(this.$store.state.user.profile, '2fa_validated', twoFactorValidated);
          
          // If 2FA is not validated, automatically generate QR code for binding
          
          if (this.showOtp2faBound === false) {
            await this.getOTP2faQrCode();
          }
        } else {
          this.showOtp2faBound = false;
          // Update store state
          this.$set(this.$store.state.user.profile, '2fa_validated', false);
          // If status check failed, still try to generate QR code
          await this.getOTP2faQrCode();
        }
      } catch (error) {
        // console.error("Error getting OTP 2FA status:", error);
        this.showOtp2faBound = false;
        // Update store state
        this.$set(this.$store.state.user.profile, '2fa_validated', false);
        // If error, still try to generate QR code
        await this.getOTP2faQrCode();
      } finally {
        // Mark as initialized after status check completes
        this.isInitialized = true;
      }
    },
    
    async getOTP2faQrCode() {
      this.loading = true;
      this.reset();
      try {
        const response = await this.$store.dispatch("user/getOTP2faQrCode");
        if (response.success && response.data) {
          // Handle different possible response structures
          // Case 1: response.data.qr_code exists
          if (response.data.qr_code) {
            this.qrCodeData = response.data.qr_code;
            this.manualSecret = response.data.secret;
          }
          // Case 2: response.data is the image directly (base64 or URL)
          else if (typeof response.data === 'string') {
            this.qrCodeData = response.data;
          }
          // Case 3: response.data is an object with image data
          else if (response.data.image || response.data.data) {
            this.qrCodeData = response.data.image || response.data.data;
            this.manualSecret = response.data.secret;
          }
          
          // If we got QR code data, focus on verification input
          if (this.qrCodeData) {
            this.$nextTick(() => {
              if (this.$refs.verification_code) {
                this.$refs.verification_code.$el.focus();
              }
            });
          } else {
            console.warn("QR code data not found in response:", response);
            this.submission.success = false;
            this.submission.status = this.$t("message.get_otp_2fa_qr_code_failed");
          }
        } else {
          this.submission.success = false;
          this.submission.status = this.$t("message." + (response.errorTag || "get_otp_2fa_qr_code_failed"));
        }
      } catch (error) {
        // console.error("Error getting QR code:", error);
        this.submission.success = false;
        this.submission.status = this.$t("message.get_otp_2fa_qr_code_failed");
      } finally {
        this.loading = false;
      }
    },
        
    async verifyOTP2fa() {
      if (!this.enableOTP) {
        return;
      }
      
      this.reset();
      this.$v.$touch();
      
      if (this.$v.$invalid) {
        return;
      }

      this.loading = true;
      
      try {
        // First verify the code
        const verifyResponse = await this.$store.dispatch("user/verifyOTP2fa", {
          code: this.verification_code
        });

        if (verifyResponse.success) {
          // Update store state immediately after successful binding
          this.$set(this.$store.state.user.profile, '2fa_validated', true);
          this.$swal({
            title: this.$t("ui.succeed"),
            text: this.$t("message.otp_2fa_bound"),
            type: "success",
            allowOutsideClick: false
          }).then(() => {
            this.loadBindingStatus();
            this.verification_code = "";
            this.enableOTP = false;
          });
        } else {
          this.submission.success = false;
          this.submission.status = this.$t("message." + (verifyResponse.errorTag || "otp_verification_failed"));
        }
      } catch (error) {
        this.submission.success = false;
        this.submission.status = this.$t("message.otp_verification_failed");
      } finally {
        this.loading = false;
      }
    },
    
    unbindOTP2fa() {
      // Show modal to get OTP code
      this.$refs.unbindOTPModal.$refs.unbindOTPModal.show();
    },
    
    onUnbindSuccess() {
      // Update store state to reflect unbind (use Vue.set for reactivity)
      this.$set(this.$store.state.user.profile, '2fa_validated', false);
      // Reload binding status to update UI
      this.loadBindingStatus();
    },
        
    cancelBinding() {
      this.qrCodeData = null;
      this.manualSecret = null;
      this.verification_code = "";
      this.enableOTP = false;
      this.showManualEntry = false;
      this.reset();
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

/* Google Authenticator Section */
.google-auth-section {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.google-auth-branding {
  margin-bottom: 0;
}

.google-auth-branding .d-flex {
  align-items: flex-start;
  gap: 16px;
}

/* Google Authenticator Logo */
.google-auth-logo {
  width: 100px;
  /* height: 60px; */
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  padding-top: 2px;
}

.google-auth-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.brand-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.brand-text {
  margin-bottom: 12px;
}

.brand-name {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.3;
  color: #202124;
  margin-bottom: 2px;
}

.brand-subtitle {
  font-size: 0.875rem;
  color: #5f6368;
  line-height: 1.4;
}

/* Download Buttons */
.download-buttons {
  gap: 12px;
  align-items: center;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: all 0.2s ease;
}

.download-btn:hover {
  text-decoration: none;
  opacity: 0.85;
  transform: translateY(-1px);
}

.download-btn img {
  height: 40px;
  width: auto;
  display: block;
}

/* Additional Info */
.additional-info {
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #e9ecef;
}

.additional-info small {
  display: block;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: #6c757d;
}

/* QR Code */
.qr-code-container {
  margin: 20px 0;
}

.qr-code-image {
  border: 2px solid #ddd;
  padding: 15px;
  background: white;
  max-width: 300px;
  width: 100%;
  border-radius: 4px;
}

/* Regenerate Section */
.regenerate-section {
  margin-top: 16px;
}

/* Help Link */
.help-link a {
  color: #1a73e8;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.help-link a:hover {
  text-decoration: underline;
}

.help-link i {
  font-size: 0.75rem;
  transition: transform 0.2s;
}

.help-link a:hover i {
  transform: translateX(2px);
}

/* Manual Entry */
.manual-entry {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
}

/* OTP Input */
.otp-input-section {
  margin-bottom: 16px;
}

.otp-input-field {
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  font-weight: bold;
  padding: 12px;
  min-width: 200px;
  flex: 1;
}

.otp-input-field::placeholder {
  font-size: 1.5rem;
  letter-spacing: 0.1rem;
  opacity: 0.6;
}

/* Verify Section */
.verify-section {
  margin-top: 16px;
}

/* Enable Checkbox */
.enable-checkbox {
  margin-top: 16px;
}

.opacity-50 {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Form Groups */
.form-group {
  margin-bottom: 2rem;
}

.form-group.mb-5 {
  margin-bottom: 3rem;
}

/* Responsive */
@media (max-width: 768px) {
  .google-auth-section {
    padding: 20px 16px;
  }
  
  .download-buttons {
    flex-direction: column;
    align-items: stretch;
  }
  
  .download-btn {
    width: 100%;
    justify-content: center;
  }
  
  .download-btn img {
    height: 40px;
  }
  
  .google-auth-branding .d-flex {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .google-auth-logo {
    margin-bottom: 16px;
    align-items: center;
  }
  
  .brand-content {
    width: 100%;
    align-items: center;
  }
  
  .brand-text {
    text-align: center;
  }
  
  .download-buttons {
    justify-content: center;
  }
}
</style>

